import React, { memo, useCallback, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Divider, Popover, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import {
  ApplicationStatus,
  loanSubtabsToUpdateOnSaveChanges,
  loanTabsToUpdateOnSaveChanges,
} from 'app/pages/loanApplication/LoanApplication.constants';
import {
  checkIsApplicationAtLeadStage,
  handleNotAcceptableResponse,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import PopTipCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopTipCDS';
import { useRequestCicReasons } from 'app/shared/modules/CicSaveReasonModal/hooks/useRequestCicReasons';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { CONFLICT_TYPE } from 'constants/http';
import { PAGE_CODES } from 'constants/pageCodes';
import { OTHERS_SAVE_PERMISSION_DENIED_MESSAGE } from 'constants/pendingChangesAuthors';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePopover from 'hooks/usePopover';
import useRevertFieldValues from 'hooks/useRevertFieldValues';
import useUrlSearchParams from 'hooks/useUrlSearchParams';
import { getConditionalObject } from 'utils/data';
import { checkIsSpecificConflictError } from 'utils/error';
import { buildPendingChangesAuthorsRequest } from 'utils/pendingChangesAuthors';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import {
  closeDialogAction,
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  cancelApplicationChangesAction,
  clearEditStateAction,
  getLoanActionsForLoanAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  getLoanAppraisalSurchargesAction,
  remountActiveTabAction,
  saveApplicationChangesAction,
} from 'store/loanApplications/actions';
import { loadCustomFieldValuesAction } from 'store/loanApplications/actions/customFieldValues.actions';
import { getApplicationChangesHistoryAction } from 'store/loanApplications/actions/loanChangesHistory.actions';
import {
  selectApplicationStatusFromDeal,
  selectIsLoanChangesNotSaved,
  selectIsLoanLocked,
  selectIsLockPendingFromPricingStatus,
  selectIsProgramChangeAllowed,
  selectIsUpdateRequestPending,
  selectProgramChangeReasons,
} from 'store/loanApplications/selectors';
import {
  selectIsCurrentUserHasPendingChanges,
  selectPendingChangesAuthorsFullNames,
} from 'store/loanApplications/selectors/pendingChanges.selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';

import { getBlockingSaveMessage } from '../../LoanHeaders.utils';
import { SendDisclosureType } from '../../LoanInfoHeader/LoanInfoHeader.constants';
import SendDisclosuresForm from '../../LoanInfoHeader/SendDisclosuresForm';
import DisabledReasonTooltip from '../DisabledReasonTooltip';
import usePendingChangesAuthorsControls from '../hooks/usePendingChangesAuthorsControls';
import { tooltipWrapperStyles } from '../LoanInfoHeader.styles';
import PendingChangesDrawer from '../PendingChangesDrawer';
import {
  PENDING_CHANGES_SUBTAB,
  PendingChangesDrawerActionLabel,
} from '../PendingChangesDrawer/PendingChangesDrawer.constants';

import PendingChangesAuthorsMenu from './components/PendingChangesAuthorsMenu';
import PendingChangesLiveSaveSummary from './components/PendingChangesLiveSaveSummary';
import {
  PendingChangesByUserMenuLabel,
  PendingChangesCancelDialogDescription,
  PendingChangesSaveDialogHeader,
} from './SavePendingChangesButton.constants';
import {
  buttonStyles,
  containerStyles,
  controlButtonStyles,
  controlsStyles,
  getPopoverStyles,
  iconButtonStyles,
  permissionMessageStyles,
} from './SavePendingChangesButton.styles';
import { SavePendingChangesButtonTestIds } from './SavePendingChangesButton.testIds';
import {
  getIsSavePendingChangesSaveDenied,
  getPendingChangesByUserMenuState,
  getSaveDisabledReason,
  getSaveDisabledTooltip,
  getSavePendingChangesTopButtonLabel,
  SAVE_PERMISSION_DENIED_REASON,
} from './SavePendingChangesButton.utils';

const popoverOptions = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
};

const SavePendingChangesButton = ({
  isLoading,
  setIsLoading,
  onClick,
  onExecuteLoanAction,
  actionData,
  isDisabled,
}) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const searchParams = useUrlSearchParams();

  const loanId = useLoanId();
  const { applicationId, tab, entityId, section, subsection } = useParams();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const revertFieldValues = useRevertFieldValues();

  const requestCicReasons = useRequestCicReasons();

  const {
    isEnabled: isPendingChangesByUserEnabled,
    currentUserId,
    authors: pendingChangesByUserAuthors,
    selectedAuthorKeys,
    ownChangesCount,
    hasOthersSelected,
    isOthersSaveAllowed,
    onToggleAuthor,
    onSelectAll,
  } = usePendingChangesAuthorsControls();

  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);

  const applicationStatus = useSelector(selectApplicationStatusFromDeal);
  const { isViewAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION));
  const hasUserPermissionToSaveOtherUsersLoanChanges = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_OTHER_USER_CHANGES),
  );
  const isCurrentUserHasPendingChanges = useSelector(selectIsCurrentUserHasPendingChanges);
  const isChangeProgramAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CHANGE_PROGRAM),
  );
  const isSaveWithoutProgramAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_WITHOUT_PROGRAM),
  );
  const isLockPending = useSelector(selectIsLockPendingFromPricingStatus);
  const isLoanLocked = useSelector(selectIsLoanLocked);
  const isProgramChangeAllowedByPricing = useSelector(selectIsProgramChangeAllowed);
  const isUpdateRequestPending = useSelector(selectIsUpdateRequestPending);
  const isLoanChangesNotSaved = useSelector(selectIsLoanChangesNotSaved);
  const programChangeReasons = useSelector(selectProgramChangeReasons);
  const pendingChangesAuthors = useSelector(selectPendingChangesAuthorsFullNames);

  const isPartialPendingChangesEnabled = useFeatureToggle(FeatureFlag.EnablePartialPendingChanges);
  const isSavePendingChangesLoanActionEnabled = useFeatureToggle(
    FeatureFlag.EnableSavePendingChangesLoanAction,
  );

  const isInitiatedByLeadStage = checkIsApplicationAtLeadStage(applicationStatus);
  const isSaveProgramPermissionsLack = !isChangeProgramAllowed && !isSaveWithoutProgramAllowed;
  const isDropdownOpened = !!anchorEl;
  const isSavePendingChangesSaveGranted = !!actionData?.grantedSave;
  const isSavePendingChangesCancelGranted = !!actionData?.grantedCancel;
  const isSavePendingChangesSaveDenied = getIsSavePendingChangesSaveDenied({
    isSavePendingChangesLoanActionEnabled,
    isSaveGranted: isSavePendingChangesSaveGranted,
  });
  const isSavePendingChangesCancelDenied =
    isSavePendingChangesLoanActionEnabled && !isSavePendingChangesCancelGranted;
  const isDisabledSaveChanges =
    isLockPending || (isLoanLocked && !isProgramChangeAllowedByPricing) || isUpdateRequestPending;
  const isPartialPendingChangesGateEnabled =
    isPartialPendingChangesEnabled && !isPendingChangesByUserEnabled;
  const isEditChangesDisabled =
    ((!isCurrentUserHasPendingChanges &&
      !hasUserPermissionToSaveOtherUsersLoanChanges &&
      isPartialPendingChangesGateEnabled) ||
      isUpdateRequestPending) &&
    !isSavePendingChangesLoanActionEnabled;
  const isViewButtonOnTop =
    isViewAllowed &&
    isEditChangesDisabled &&
    isPartialPendingChangesGateEnabled &&
    !isSavePendingChangesLoanActionEnabled;
  const isViewDisabled =
    isEditChangesDisabled &&
    !isViewAllowed &&
    isPartialPendingChangesGateEnabled &&
    !isSavePendingChangesLoanActionEnabled;
  const hasOwnPendingChanges = isCurrentUserHasPendingChanges || ownChangesCount > 0;
  const hasNoOwnChangesToSave = isPendingChangesByUserEnabled && !hasOwnPendingChanges;
  const isTopButtonDisabled =
    isLoading ||
    isDisabled ||
    hasNoOwnChangesToSave ||
    (isSavePendingChangesLoanActionEnabled
      ? isDisabledSaveChanges || isSavePendingChangesSaveDenied
      : isViewDisabled || (!isViewButtonOnTop && isDisabledSaveChanges));
  const isSaveActionDisabled =
    isLoading || isDisabled || isDisabledSaveChanges || isEditChangesDisabled;
  const topButtonLabel = getSavePendingChangesTopButtonLabel({ isViewButtonOnTop });
  const pendingChangesByUserMenuState = getPendingChangesByUserMenuState({
    isBusy: isLoading || isDisabled,
    isSaveBlocked: isDisabledSaveChanges,
    isCancelBlocked: isEditChangesDisabled,
    isLoanActionEnabled: isSavePendingChangesLoanActionEnabled,
    isSaveGranted: isSavePendingChangesSaveGranted,
    isCancelGranted: isSavePendingChangesCancelGranted,
    hasOwnChanges: hasOwnPendingChanges,
    hasOthersSelected,
    isOthersSaveAllowed,
  });

  const saveDisabledReason = useMemo(
    () =>
      getSaveDisabledReason({
        isLoading,
        isLockPending,
        isLoanLocked,
        isProgramChangeAllowed: isProgramChangeAllowedByPricing,
        isUpdateRequestPending,
        isViewDisabled,
        isEditChangesDisabled,
        programChangeReasons,
        pendingChangesAuthors,
        hasNoOwnChanges: hasNoOwnChangesToSave,
      }),
    [
      isLoading,
      isLockPending,
      isLoanLocked,
      isProgramChangeAllowedByPricing,
      isUpdateRequestPending,
      isViewDisabled,
      isEditChangesDisabled,
      programChangeReasons,
      pendingChangesAuthors,
      hasNoOwnChangesToSave,
    ],
  );
  const disabledReasonTooltip = useMemo(
    () =>
      saveDisabledReason ? (
        <DisabledReasonTooltip
          hint={saveDisabledReason.hint}
          reasons={saveDisabledReason.reasons}
          title={saveDisabledReason.title}
        />
      ) : (
        ''
      ),
    [saveDisabledReason],
  );
  const permissionDeniedTooltip = useMemo(
    () => (
      <DisabledReasonTooltip
        hint={SAVE_PERMISSION_DENIED_REASON.hint}
        reasons={SAVE_PERMISSION_DENIED_REASON.reasons}
        title={SAVE_PERMISSION_DENIED_REASON.title}
      />
    ),
    [],
  );
  const topButtonTooltip = getSaveDisabledTooltip({
    isSaveDenied: isSavePendingChangesSaveDenied,
    permissionTooltip: permissionDeniedTooltip,
    isDisabled: isTopButtonDisabled && !isViewButtonOnTop,
    disabledTooltip: disabledReasonTooltip,
  });
  const isSaveMenuActionDisabled = isPendingChangesByUserEnabled
    ? isSaveActionDisabled || pendingChangesByUserMenuState.isSaveMineDisabled
    : isSaveActionDisabled;
  const saveActionTooltip = isSaveMenuActionDisabled ? disabledReasonTooltip : '';
  const drawerSaveTooltip = getSaveDisabledTooltip({
    isSaveDenied: isSavePendingChangesSaveDenied,
    permissionTooltip: permissionDeniedTooltip,
    isDisabled: isSaveMenuActionDisabled,
    disabledTooltip: disabledReasonTooltip,
  });

  useEffectSafeStrictMode(() => {
    return () => {
      dispatch(getLoanActionsForLoanAction({ applicationId }));
    };
  }, [dispatch, applicationId]);

  const onOpenPendingChangesHistory = useCallback(() => {
    onClearAnchorElement();
    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: PENDING_CHANGES_SUBTAB,
        entityId,
        section,
        subsection,
      }),
    );
  }, [onClearAnchorElement, history, applicationId, tab, entityId, section, subsection]);

  const cancelChangesByAuthors = useCallback(
    ({ authorKeys = null, description = PendingChangesCancelDialogDescription.ALL } = {}) => {
      onClearAnchorElement();

      const isCurrentUserChangesCancelled = !authorKeys || authorKeys.includes(currentUserId);

      const dialogOptions = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm cancellation of changes',
        description,
        onAccept: () => {
          setIsLoading(true);

          if (localEditedFieldPaths.length && isCurrentUserChangesCancelled) {
            revertFieldValues(localEditedFieldPaths);
            dispatch(clearLocalEditedFieldsAction());
          }

          dispatch(
            cancelApplicationChangesAction({
              loanId,
              applicationId,
              ...getConditionalObject(!!authorKeys, () => ({
                authors: buildPendingChangesAuthorsRequest(authorKeys),
              })),
            }),
          )
            .then(() => dispatch(getLoanAppraisalSurchargesAction({ loanId, applicationId })))
            .then(() => {
              batch(() => {
                dispatch(clearEditStateAction());
                dispatch(remountActiveTabAction({ triggeredByPendingChanges: true }));

                if (authorKeys) {
                  dispatch(getApplicationChangesHistoryAction({ applicationId }));
                }
              });
            })
            .catch(Error)
            .finally(() => {
              setIsLoading(false);
            });
        },
      };

      dispatch(openConfirmationDialogAction(dialogOptions));
    },
    [
      onClearAnchorElement,
      dispatch,
      setIsLoading,
      loanId,
      applicationId,
      localEditedFieldPaths,
      revertFieldValues,
      currentUserId,
    ],
  );

  const onCancelChanges = useCallback(() => cancelChangesByAuthors(), [cancelChangesByAuthors]);

  const onCancelMyChanges = useCallback(
    () =>
      cancelChangesByAuthors({
        authorKeys: [currentUserId],
        description: PendingChangesCancelDialogDescription.MINE,
      }),
    [cancelChangesByAuthors, currentUserId],
  );

  const onCancelSelectedChanges = useCallback(
    () =>
      cancelChangesByAuthors({
        authorKeys: selectedAuthorKeys,
        description: PendingChangesCancelDialogDescription.SELECTED,
      }),
    [cancelChangesByAuthors, selectedAuthorKeys],
  );

  const openSendDelayedDisclosuresDialog = useCallback(
    delayedCompletionDate => {
      const delayCompletionFormattedDate =
        dayjs(delayedCompletionDate).format(COMMON_DATETIME_FORMAT);
      const title = `Disclosure will be sent on ${delayCompletionFormattedDate}. Would you like to send it now?`;

      dispatch(
        openDialogAction({
          header: 'Send delayed disclosures',
          body: dialogProps => (
            <SendDisclosuresForm
              {...dialogProps}
              disclosureType={SendDisclosureType.DELAYED}
              loanId={loanId}
              title={title}
            />
          ),
        }),
      );
    },
    [dispatch, loanId],
  );

  const savePendingChangesWithLockDate = useCallback(
    initialActionData => {
      const saveWithLockDate = actionData => {
        const successMessage = actionData.isInitiatedByLeadStage
          ? 'All changes in the lead are applied to the lead application'
          : 'All changes in the loan are applied to the loan application';

        setIsLoading(true);

        return dispatch(saveApplicationChangesAction(actionData))
          .then(async ({ payload }) => {
            const { metadata } = payload || {};

            if (metadata?.isSaved === false && metadata?.cicReasonRequired) {
              const resaveData = await requestCicReasons(metadata, actionData);

              if (!resaveData) {
                return;
              }

              return saveWithLockDate(resaveData);
            }

            const blockingSaveMessage = getBlockingSaveMessage(metadata);

            if (blockingSaveMessage) {
              dispatch(showMessageAction({ message: blockingSaveMessage, variant: 'error' }));

              return;
            }

            if (loanId) {
              await dispatch(getLoanAppraisalSurchargesAction({ loanId, applicationId }));
            }

            batch(() => {
              dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
              dispatch(closeDialogAction());
              dispatch(
                remountActiveTabAction({
                  tabsToRemount: loanTabsToUpdateOnSaveChanges,
                  subtabsToRemount: loanSubtabsToUpdateOnSaveChanges,
                  searchParams,
                  triggeredByPendingChanges: true,
                }),
              );
              dispatch(getLoanApplicationHeaderViewAction({ applicationId }));
              dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
              dispatch(loadCustomFieldValuesAction({ applicationId }));

              if (actionData.authors) {
                dispatch(getApplicationChangesHistoryAction({ applicationId }));
              }
            });

            const { delayedCompletionDate } = payload?.loan || {};
            const isApplicationStatusSecondPartCompleted =
              payload?.status === ApplicationStatus.SECOND_PART_COMPLETED;

            if (delayedCompletionDate && !isApplicationStatusSecondPartCompleted) {
              openSendDelayedDisclosuresDialog(delayedCompletionDate);
            }
          })
          .catch(({ response }) => {
            const isClosingDisclosureBeingWorkedOnConflictErrorOccurred =
              checkIsSpecificConflictError(
                response,
                CONFLICT_TYPE.CLOSING_DISCLOSURE_IS_BEING_WORKED_ON,
              );

            if (isClosingDisclosureBeingWorkedOnConflictErrorOccurred) {
              dispatch(showMessageAction({ message: response.data.message, variant: 'error' }));

              return;
            }

            return handleNotAcceptableResponse({
              dispatch,
              response,
              onAcceptAction:
                (...args) =>
                () =>
                  saveWithLockDate(...args),
              isSuccessActionNeededAfterAccept: false,
              actionData,
              isSaveWithoutProgramNeededOnNoPrograms: true,
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      };

      return saveWithLockDate(initialActionData);
    },
    [
      setIsLoading,
      dispatch,
      searchParams,
      applicationId,
      loanId,
      openSendDelayedDisclosuresDialog,
      requestCicReasons,
    ],
  );

  const saveChangesByAuthors = useCallback(
    (authors = null) => {
      onClearAnchorElement();

      const authorsData = getConditionalObject(!!authors, { authors });

      if (isInitiatedByLeadStage) {
        return savePendingChangesWithLockDate({
          applicationId,
          saveWithoutProgram: true,
          isInitiatedByLeadStage,
          ...authorsData,
        });
      }

      return savePendingChangesWithLockDate({
        applicationId,
        saveWithoutProgram: isSaveProgramPermissionsLack ? false : undefined,
        ...authorsData,
      });
    },
    [
      savePendingChangesWithLockDate,
      isSaveProgramPermissionsLack,
      applicationId,
      isInitiatedByLeadStage,
      onClearAnchorElement,
    ],
  );

  const onSaveChanges = useCallback(() => saveChangesByAuthors(), [saveChangesByAuthors]);

  const openSaveSummaryDialog = useCallback(
    ({ authorKeys, isOwnChangesScope }) => {
      onClearAnchorElement();

      const authors = buildPendingChangesAuthorsRequest(authorKeys);
      const saveButtonName = isOwnChangesScope
        ? PendingChangesByUserMenuLabel.SAVE_MINE
        : PendingChangesByUserMenuLabel.SAVE_SELECTED;

      const dialogId = dispatch(
        openDialogAction({
          header: isOwnChangesScope
            ? PendingChangesSaveDialogHeader.MINE
            : PendingChangesSaveDialogHeader.SELECTED,
          isRestyled: true,
          size: ModalSize.SMALL,
          withButtons: true,
          dataTestId: SavePendingChangesButtonTestIds.SAVE_SUMMARY_MODAL,
          acceptButtonDataTestId: SavePendingChangesButtonTestIds.SAVE_SUMMARY_ACCEPT_BUTTON,
          onAcceptButtonName: saveButtonName,
          onAcceptClick: () => {
            dispatch(closeDialogAction(dialogId));

            if (isSavePendingChangesLoanActionEnabled) {
              onExecuteLoanAction?.(actionData?.id, { pendingChangesAuthors: authors });

              return;
            }

            saveChangesByAuthors(authors);
          },
          body: () => (
            <PendingChangesLiveSaveSummary
              authorKeys={authorKeys}
              isOwnChangesScope={isOwnChangesScope}
            />
          ),
        }),
      );
    },
    [
      onClearAnchorElement,
      dispatch,
      isSavePendingChangesLoanActionEnabled,
      onExecuteLoanAction,
      actionData,
      saveChangesByAuthors,
    ],
  );

  const onSaveMyChanges = useCallback(
    () => openSaveSummaryDialog({ authorKeys: [currentUserId], isOwnChangesScope: true }),
    [openSaveSummaryDialog, currentUserId],
  );

  const onSaveSelectedChanges = useCallback(
    () => openSaveSummaryDialog({ authorKeys: selectedAuthorKeys, isOwnChangesScope: false }),
    [openSaveSummaryDialog, selectedAuthorKeys],
  );

  const defaultClickHandler = isViewButtonOnTop ? onOpenPendingChangesHistory : onSaveChanges;
  const legacyTopButtonOnClick = isSavePendingChangesLoanActionEnabled
    ? onClick
    : defaultClickHandler;
  const topButtonOnClick = isPendingChangesByUserEnabled ? onSaveMyChanges : legacyTopButtonOnClick;

  if (!isLoanChangesNotSaved) {
    return null;
  }

  return (
    <>
      <Box sx={containerStyles}>
        <PopTipCDS title={topButtonTooltip}>
          <Box component="span" sx={tooltipWrapperStyles}>
            <Button
              color="primary"
              data-id={actionData?.id}
              data-testid={SavePendingChangesButtonTestIds.SAVE_BUTTON}
              disabled={isTopButtonDisabled}
              name="Save Pending Changes"
              sx={buttonStyles}
              variant="contained"
              onClick={topButtonOnClick}
            >
              {topButtonLabel}
            </Button>
          </Box>
        </PopTipCDS>
        <Button
          color="primary"
          data-testid={SavePendingChangesButtonTestIds.TOGGLE_BUTTON}
          disabled={isLoading || isDisabled || isViewDisabled}
          sx={iconButtonStyles}
          variant="contained"
          onClick={onSetAnchorElement}
        >
          <ArrowCheckIcon
            direction={isDropdownOpened ? IconDirection.UP : IconDirection.DOWN}
            size={IconSize[14]}
          />
        </Button>
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={popoverOptions.anchorOrigin}
        open={!!anchorEl}
        sx={getPopoverStyles({ isView: isViewButtonOnTop })}
        transformOrigin={popoverOptions.transformOrigin}
        onClose={onClearAnchorElement}
      >
        {isPendingChangesByUserEnabled && (
          <Box sx={controlsStyles}>
            <PendingChangesAuthorsMenu
              authors={pendingChangesByUserAuthors}
              isDisabled={isLoading || isDisabled}
              selectedAuthorKeys={selectedAuthorKeys}
              onSelectAll={onSelectAll}
              onToggleAuthor={onToggleAuthor}
            />
            <Divider />
            {!pendingChangesByUserMenuState.isSaveMineHidden && (
              <PopTipCDS placement="left" title={saveActionTooltip}>
                <Box component="span" sx={tooltipWrapperStyles}>
                  <Button
                    data-id={actionData?.id}
                    data-testid={SavePendingChangesButtonTestIds.SAVE_CHANGES_BUTTON}
                    disabled={isSaveMenuActionDisabled}
                    name={PendingChangesByUserMenuLabel.SAVE_MINE}
                    sx={controlButtonStyles}
                    variant="text"
                    onClick={onSaveMyChanges}
                  >
                    {`${PendingChangesByUserMenuLabel.SAVE_MINE} (${ownChangesCount})`}
                  </Button>
                </Box>
              </PopTipCDS>
            )}
            <Button
              data-testid={SavePendingChangesButtonTestIds.SEE_CHANGES_BUTTON}
              disabled={isLoading || isDisabled}
              sx={controlButtonStyles}
              variant="text"
              onClick={onOpenPendingChangesHistory}
            >
              See Changes
            </Button>
            {!pendingChangesByUserMenuState.isCancelMineHidden && (
              <Button
                data-testid={SavePendingChangesButtonTestIds.CANCEL_CHANGES_BUTTON}
                disabled={pendingChangesByUserMenuState.isCancelMineDisabled}
                sx={controlButtonStyles}
                variant="text"
                onClick={onCancelMyChanges}
              >
                {PendingChangesByUserMenuLabel.CANCEL_MINE}
              </Button>
            )}
            {hasOthersSelected && <Divider />}
            {!pendingChangesByUserMenuState.isSaveSelectedHidden && (
              <Button
                data-testid={SavePendingChangesButtonTestIds.SAVE_SELECTED_CHANGES_BUTTON}
                disabled={pendingChangesByUserMenuState.isSaveSelectedDisabled}
                sx={controlButtonStyles}
                variant="text"
                onClick={onSaveSelectedChanges}
              >
                {PendingChangesByUserMenuLabel.SAVE_SELECTED}
              </Button>
            )}
            {!pendingChangesByUserMenuState.isCancelSelectedHidden && (
              <Button
                data-testid={SavePendingChangesButtonTestIds.CANCEL_SELECTED_CHANGES_BUTTON}
                disabled={pendingChangesByUserMenuState.isCancelSelectedDisabled}
                sx={controlButtonStyles}
                variant="text"
                onClick={onCancelSelectedChanges}
              >
                {PendingChangesByUserMenuLabel.CANCEL_SELECTED}
              </Button>
            )}
            {pendingChangesByUserMenuState.isOthersPermissionMessageShown && (
              <Typography color="text.secondary" sx={permissionMessageStyles} variant="regular12">
                {OTHERS_SAVE_PERMISSION_DENIED_MESSAGE}
              </Typography>
            )}
          </Box>
        )}
        {!isPendingChangesByUserEnabled && (
          <Box sx={controlsStyles}>
            {(!isSavePendingChangesLoanActionEnabled || isSavePendingChangesSaveGranted) && (
              <PopTipCDS placement="left" title={saveActionTooltip}>
                <Box component="span" sx={tooltipWrapperStyles}>
                  <Button
                    data-id={actionData?.id}
                    data-testid={SavePendingChangesButtonTestIds.SAVE_CHANGES_BUTTON}
                    disabled={isSaveActionDisabled}
                    name="Save Pending Changes"
                    sx={controlButtonStyles}
                    variant="text"
                    onClick={isSavePendingChangesLoanActionEnabled ? onClick : onSaveChanges}
                  >
                    Save Changes
                  </Button>
                </Box>
              </PopTipCDS>
            )}
            <Button
              data-testid={SavePendingChangesButtonTestIds.SEE_CHANGES_BUTTON}
              disabled={
                isLoading ||
                isDisabled ||
                (!isViewAllowed &&
                  isPartialPendingChangesEnabled &&
                  !isSavePendingChangesLoanActionEnabled)
              }
              sx={controlButtonStyles}
              variant="text"
              onClick={onOpenPendingChangesHistory}
            >
              See Changes
            </Button>
            {(!isSavePendingChangesLoanActionEnabled || isSavePendingChangesCancelGranted) && (
              <Button
                data-testid={SavePendingChangesButtonTestIds.CANCEL_CHANGES_BUTTON}
                disabled={isLoading || isDisabled || isEditChangesDisabled}
                sx={controlButtonStyles}
                variant="text"
                onClick={onCancelChanges}
              >
                Cancel Changes
              </Button>
            )}
          </Box>
        )}
      </Popover>

      <PendingChangesDrawer
        cancelButtonLabel={
          isPendingChangesByUserEnabled
            ? PendingChangesByUserMenuLabel.CANCEL_MINE
            : PendingChangesDrawerActionLabel.CANCEL
        }
        isCancelDisabled={
          isPendingChangesByUserEnabled
            ? pendingChangesByUserMenuState.isCancelMineDisabled
            : isLoading || isDisabled || isEditChangesDisabled
        }
        isCancelHidden={isSavePendingChangesCancelDenied}
        isSaveDisabled={isSaveMenuActionDisabled || isSavePendingChangesSaveDenied}
        saveButtonLabel={
          isPendingChangesByUserEnabled
            ? PendingChangesByUserMenuLabel.SAVE_MINE
            : PendingChangesDrawerActionLabel.SAVE
        }
        saveDisabledTooltip={drawerSaveTooltip}
        onCancelChanges={isPendingChangesByUserEnabled ? onCancelMyChanges : onCancelChanges}
        onSaveChanges={isPendingChangesByUserEnabled ? onSaveMyChanges : onSaveChanges}
      />
    </>
  );
};

SavePendingChangesButton.propTypes = {
  actionData: PropTypes.object,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func,
  onExecuteLoanAction: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default withLoading(memo(SavePendingChangesButton));
